import { DateTime } from 'luxon';

import { HealthGoal } from 'src/models/health-goals/HealthGoal';
import { MemberGoalCompletedResult } from 'src/models/health-goals/MemberGoalCompletedResult';
import { MemberGoalResult } from 'src/models/health-goals/MemberGoalResult';
import { CompletionLabel } from 'src/models/health-goals/CompletionLabel';
import TrackerHypertensionIcon from 'src/components/atoms/webmdhs-icon/tracker-hypertension-icon/TrackerHypertensionIcon';
import { Icon } from 'src/ui-models/Icon';
import TrackerDiabetesIcon from 'src/components/atoms/webmdhs-icon/tracker-diabetes-icon/TrackerDiabetesIcon';
import Accessibility24pxIcon from 'src/components/atoms/webmdhs-icon/accessibility-24px-icon/Accessibility24pxIcon';
import CholesterolIcon from 'src/components/atoms/webmdhs-icon/cholesterol-icon/CholesterolIcon';
import TrackerTobaccoIcon from 'src/components/atoms/webmdhs-icon/tracker-tobacco-icon/TrackerTobaccoIcon';
import { NutritionIcon, SleepIcon, StressIcon } from 'src/components/atoms/webmdhs-icon';
import HeightIcon from 'src/components/atoms/webmdhs-icon/height-icon/HeightIcon';
import WbSunnyIcon from 'src/components/atoms/webmdhs-icon/wb-sunny-icon/WbSunnyIcon';
import HearthFailureIcon from 'src/components/atoms/webmdhs-icon/hearth-failure-icon/HearthFailureIcon';
import StraightenIcon from 'src/components/atoms/webmdhs-icon/straighten-icon/StraightenIcon';
import WeightIcon from 'src/components/atoms/webmdhs-icon/weight-icon/WeightIcon';
import RemoveRedEyeIcon from 'src/components/atoms/webmdhs-icon/remove-red-eye-icon/RemoveRedEyeIcon';
import Assignment24pxIcon from 'src/components/atoms/webmdhs-icon/assignment24px-icon/Assignment24pxIcon';
import VaccinesIcon from 'src/components/atoms/webmdhs-icon/vaccines-icon/VaccinesIcon';
import StepsIcon from 'src/components/atoms/webmdhs-icon/steps-icon/StepsIcon';
import TrackerExerciseIcon from 'src/components/atoms/webmdhs-icon/tracker-exercise-icon/TrackerExerciseIcon';
import BiometricsIcon from 'src/components/atoms/webmdhs-icon/biomectrics-icon/BiometricsIcon';
import { BiometricTestType, CalculatedType, HealthGoalRate, HealthGoalType } from './enums';
import { ERROR_MESSAGES, HEALTH_GOAL_RATE_TEXT } from './constants';
import {
  DATE_FIELD,
  DEFAULT_TIME_ZONE,
  SHORT_MONTH_DAY_FORMAT,
  SHORT_MONTH_DAY_YEAR_FORMAT,
  getDateTimeField,
  getFormattedDate,
  parseDateTime,
} from './friendlyDateFormatter';

export const compareHealthGoals = (
  currentGoal: MemberGoalCompletedResult | MemberGoalResult,
  nextGoal: MemberGoalCompletedResult | MemberGoalResult,
): number => {
  if (currentGoal.phrTitle < nextGoal.phrTitle) {
    return -1;
  }
  if (currentGoal.phrTitle > nextGoal.phrTitle) {
    return 1;
  }
  return 0;
};

export function extractPinnedGoals(healthGoal: HealthGoal): (MemberGoalResult | MemberGoalCompletedResult)[] {
  const allGoals = [
    ...(healthGoal?.completedHealthGoals ?? []),
    ...(healthGoal?.activeHealthGoals ?? []),
    ...(healthGoal?.endedHealthGoals ?? []),
  ];
  return allGoals.filter(goal => goal.pinnedDate !== null);
}

const getRateFrequencyText = (calculatedType: CalculatedType, rate: HealthGoalRate): string => {
  if (calculatedType === 'Cumulative') {
    return rate === HealthGoalRate.PerDateRange ? '' : 'in a';
  }

  return rate === HealthGoalRate.PerDateRange ? 'per day' : 'per day for a';
};

const getEvaluationDateRangeText = (pinnedHealthGoal: MemberGoalResult | MemberGoalCompletedResult): string => {
  const calculatedTypeText
    = pinnedHealthGoal.calculatedType === CalculatedType.Cumulative ? 'Get' : pinnedHealthGoal.calculatedType;
  const goalTypeText = pinnedHealthGoal.healthGoalType === HealthGoalType.StepsGoal ? 'steps' : 'minutes of exercise';

  return `${calculatedTypeText} ${pinnedHealthGoal.calculatedTarget.toLocaleString()} ${goalTypeText} ${getRateFrequencyText(
    pinnedHealthGoal.calculatedType,
    pinnedHealthGoal.rate,
  )}`;
};

const getSubtitleForStepsAndExerciseGoals = (
  pinnedHealthGoal: MemberGoalResult | MemberGoalCompletedResult,
): string => {
  if (pinnedHealthGoal.rate === HealthGoalRate.PerDateRange) {
    const startDate = getFormattedDate(pinnedHealthGoal.healthGoalStartDate);
    const endDate = getFormattedDate(pinnedHealthGoal.healthGoalEndDate);

    return `${getEvaluationDateRangeText(pinnedHealthGoal).trim()} - ${startDate} - ${endDate}`;
  }

  return `${getEvaluationDateRangeText(pinnedHealthGoal)} ${
    HEALTH_GOAL_RATE_TEXT[pinnedHealthGoal.rate as keyof typeof HEALTH_GOAL_RATE_TEXT]
  }`;
};

export const getSubtitle = (pinnedHealthGoal: MemberGoalResult | MemberGoalCompletedResult): string => {
  if (pinnedHealthGoal.customSubtitle) {
    return pinnedHealthGoal.customSubtitle;
  }

  switch (pinnedHealthGoal.healthGoalType) {
    case HealthGoalType.StepsGoal:
    case HealthGoalType.ExerciseGoal:
      return getSubtitleForStepsAndExerciseGoals(pinnedHealthGoal);
    case HealthGoalType.BiometricScreening:
      return 'Complete a Biometric Screening';
    case HealthGoalType.PreventiveTests:
      return pinnedHealthGoal.biometricTest;
    default:
      throw new Error(ERROR_MESSAGES.invalidHealthGoalType(pinnedHealthGoal.healthGoalType));
  }
};

export const getCalendarRange = (rate: HealthGoalRate, startDate: string, endDate: string): string => {
  const isSameMonth = getDateTimeField(startDate, DATE_FIELD.Month) === getDateTimeField(endDate, DATE_FIELD.Month);
  const isSameYear = getDateTimeField(startDate, DATE_FIELD.Year) === getDateTimeField(endDate, DATE_FIELD.Year);

  const startShort = getFormattedDate(startDate, SHORT_MONTH_DAY_FORMAT);
  const endShort = getFormattedDate(endDate, SHORT_MONTH_DAY_FORMAT);
  const startFull = getFormattedDate(startDate, SHORT_MONTH_DAY_YEAR_FORMAT);
  const endFull = getFormattedDate(endDate, SHORT_MONTH_DAY_YEAR_FORMAT);

  switch (rate) {
    case HealthGoalRate.PerDay:
      return startShort;

    case HealthGoalRate.PerCalWeek:
    case HealthGoalRate.PerCalMonth:
      return isSameMonth
        ? `${startShort} - ${getDateTimeField(endDate, DATE_FIELD.Day)}`
        : `${startShort} - ${endShort}`;

    case HealthGoalRate.PerCalQuarter:
      return `Q${getDateTimeField(startDate, DATE_FIELD.Quarter)}: ${startShort} - ${endShort}`;

    case HealthGoalRate.PerCalYear:
      return isSameYear ? `${startShort} - ${endFull}` : `${startFull} - ${endFull}`;

    case HealthGoalRate.PerDateRange:
      return `${startFull} - ${endFull}`;
  }
};

export const getCalculatedUnit = (calculatedType: CalculatedType): string => {
  return calculatedType === CalculatedType.Average ? 'AVERAGE' : 'TOTAL';
};

export const getMeasurementUnit = (rate: HealthGoalRate, healthGoalType: HealthGoalType): string => {
  const rateText = HEALTH_GOAL_RATE_TEXT[rate as keyof typeof HEALTH_GOAL_RATE_TEXT];
  const unit = (() => {
    switch (healthGoalType) {
      case HealthGoalType.StepsGoal:
        return 'steps';
      case HealthGoalType.ExerciseGoal:
        return 'min';
      default:
        return 'N/A';
    }
  })();

  return rateText ? `${unit}/${rateText}` : unit;
};

export const getProgressValue = (
  rate: HealthGoalRate,
  currentMeasurementCount: number,
  calculatedTarget: number,
): number | null => {
  if (rate !== HealthGoalRate.PerDay) return null;
  if (!calculatedTarget || calculatedTarget <= 0) return null;

  return (currentMeasurementCount / calculatedTarget) * 100;
};

export const isCompleted = (pinnedHealthGoal: MemberGoalResult | MemberGoalCompletedResult): boolean => {
  const isMemberGoalCompletedResult = 'completedDate' in pinnedHealthGoal;
  return isMemberGoalCompletedResult && !!pinnedHealthGoal.completedDate;
};

export const isEnded = (pinnedHealthGoal: MemberGoalResult | MemberGoalCompletedResult): boolean => {
  if (!pinnedHealthGoal.healthGoalEndDate) return false;

  const endDateTime = parseDateTime(pinnedHealthGoal.healthGoalEndDate).setZone(DEFAULT_TIME_ZONE);
  const now = DateTime.now().setZone(DEFAULT_TIME_ZONE);

  return now > endDateTime;
};

const getCompletionLabelForPreventiveTests = (
  pinnedHealthGoal: MemberGoalResult | MemberGoalCompletedResult,
): CompletionLabel => {
  const emptyCompletionLabel = { text: '', completionTimes: '', isCompleted: false };

  if (isCompleted(pinnedHealthGoal) || isEnded(pinnedHealthGoal) || pinnedHealthGoal.suppressRecommendations) {
    return emptyCompletionLabel;
  }

  const { testRecommendedBy, isMemberUpToDate } = pinnedHealthGoal;

  if (testRecommendedBy) {
    const now = DateTime.now().setZone(DEFAULT_TIME_ZONE).startOf(DATE_FIELD.Day);
    const recommendedDate = DateTime.fromISO(testRecommendedBy, { zone: DEFAULT_TIME_ZONE }).startOf(DATE_FIELD.Day);
    const thresholdDate = recommendedDate.minus({ days: 90 });

    if (now >= thresholdDate) {
      return {
        text: `Recommended by: ${getFormattedDate(testRecommendedBy, 'M/d/yyyy')}`,
        completionTimes: '',
        isCompleted: false,
      };
    }
  }

  return isMemberUpToDate
    ? { text: "You're up-to-date!", completionTimes: '', isCompleted: true }
    : emptyCompletionLabel;
};

export const getCompletionLabel = (pinnedHealthGoal: MemberGoalResult | MemberGoalCompletedResult): CompletionLabel => {
  const { totalCompletions } = pinnedHealthGoal;
  const emptyCompletionLabel = { text: '', completionTimes: '', isCompleted: false };

  switch (pinnedHealthGoal.healthGoalType) {
    case HealthGoalType.StepsGoal:
    case HealthGoalType.ExerciseGoal:
    case HealthGoalType.BiometricTest:
    case HealthGoalType.BiometricTestCurrent:
    case HealthGoalType.TrackingGoal:
      if (totalCompletions > 0) {
        const total = totalCompletions > 99 ? '99+' : totalCompletions.toString();

        return {
          text: `Achieved ${total} ${totalCompletions > 1 ? 'times' : 'time'}`,
          completionTimes: total,
          isCompleted: true,
        };
      }

      return emptyCompletionLabel;
    case HealthGoalType.PreventiveTests:
      return getCompletionLabelForPreventiveTests(pinnedHealthGoal);
    case HealthGoalType.BiometricScreening:
    default:
      return emptyCompletionLabel;
  }
};

export const getGoalRepeatabilityRateText = (rate: HealthGoalRate): string => {
  const rateTexts = {
    [HealthGoalRate.PerDay]: 'Achievable daily',
    [HealthGoalRate.PerCalWeek]: 'Achievable weekly',
    [HealthGoalRate.PerCalMonth]: 'Achievable monthly',
    [HealthGoalRate.PerCalQuarter]: 'Achievable quarterly',
    [HealthGoalRate.PerCalYear]: 'Achievable yearly',
    [HealthGoalRate.PerDateRange]: '',
  };
  return rateTexts[rate] || '';
};

const getIconBasedOnBiometricTestId = (biometricTestId: number): Icon => {
  switch (biometricTestId as BiometricTestType) {
    case BiometricTestType.BloodPressureCombined:
    case BiometricTestType.BloodPressureDiastolic:
    case BiometricTestType.BloodPressureSystolic:
      return { icon: TrackerHypertensionIcon, color: '#2473D4' };
    case BiometricTestType.BloodSugarCombined:
    case BiometricTestType.BloodSugarFasting:
    case BiometricTestType.BloodSugarNonFasting:
      return { icon: TrackerDiabetesIcon, color: '#D1414E' };
    case BiometricTestType.BMI:
    case BiometricTestType.BodyFatPercentage:
      return { icon: Accessibility24pxIcon, color: '#007EA2' };
    case BiometricTestType.CholesterolRatio:
    case BiometricTestType.HDLCholesterol:
    case BiometricTestType.LDLCholesterol:
    case BiometricTestType.TotalCholesterol:
    case BiometricTestType.Triglycerides:
      return { icon: CholesterolIcon, color: '#CE2955' };
    case BiometricTestType.CotinineBloodDraw:
    case BiometricTestType.CotinineGeneric:
    case BiometricTestType.CotinineSalivaTest:
    case BiometricTestType.CotinineUrineTest:
      return { icon: TrackerTobaccoIcon, color: '#895BBE' };
    case BiometricTestType.Diet:
      return { icon: NutritionIcon, color: '#AE2573' };
    case BiometricTestType.Height:
      return { icon: HeightIcon, color: '#007EA2' };
    case BiometricTestType.Mood:
      return { icon: WbSunnyIcon, color: '#007CB6' };
    case BiometricTestType.RestingHeartRate:
      return { icon: HearthFailureIcon, color: '#068266' };
    case BiometricTestType.Stress:
    case BiometricTestType.MindfulnessMinutesTest:
      return { icon: StressIcon, color: '#007CB6' };
    case BiometricTestType.WaistMeasurement:
      return { icon: StraightenIcon, color: '#007EA2' };
    case BiometricTestType.Weight:
      return { icon: WeightIcon, color: '#007EA2' };
    case BiometricTestType.HoursOfSleep:
      return { icon: SleepIcon, color: '#3D62C5' };
    case BiometricTestType.FluShot:
    case BiometricTestType.Covid19Immunization:
    case BiometricTestType.PneumococcalVaccine:
    case BiometricTestType.ShinglesVaccine:
      return { icon: VaccinesIcon, color: '#068266' };
    case BiometricTestType.VisionExam:
    case BiometricTestType.GlaucomaScreening:
      return { icon: RemoveRedEyeIcon, color: '#068266' };
    default:
      return { icon: Assignment24pxIcon, color: '#068266' };
  }
};

export const getIcon = (healthGoalType: HealthGoalType, biometricTestId: number): Icon => {
  switch (healthGoalType) {
    case HealthGoalType.StepsGoal:
      return { icon: StepsIcon, color: '#6467D9' };
    case HealthGoalType.ExerciseGoal:
      return { icon: TrackerExerciseIcon, color: '#6467D9' };
    case HealthGoalType.BiometricScreening:
      return { icon: BiometricsIcon, color: '#068266' };
    case HealthGoalType.BiometricTest:
    case HealthGoalType.BiometricTestCurrent:
    case HealthGoalType.TrackingGoal:
    case HealthGoalType.PreventiveTests:
      return getIconBasedOnBiometricTestId(biometricTestId);
    default:
      return { icon: Assignment24pxIcon, color: '#068266' };
  }
};
